// services/dashboardService.js
import { dashboardAPI } from './api';

export const dashboardService = {
  /**
   * Get dashboard statistics
   */
  async getDashboardStats() {
    try {
      console.log('📊 Fetching dashboard statistics...');
      const response = await dashboardAPI.getStats();
      console.log('✅ Dashboard stats loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching dashboard statistics:', error);
      
      // Enhanced error handling
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view dashboard statistics.');
      } else if (error.isTimeout) {
        throw new Error('Request timeout. Please check your connection.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load dashboard statistics');
      }
    }
  },

  /**
   * Get recent surveys
   */
  async getRecentSurveys() {
    try {
      console.log('📋 Fetching recent surveys...');
      const response = await dashboardAPI.getRecentSurveys();
      console.log('✅ Recent surveys loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching recent surveys:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view recent surveys.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load recent surveys');
      }
    }
  },

  /**
   * Get chart data for specific chart type
   */
  async getChartData(chartType, timeframe = '30d') {
    try {
      console.log(`📈 Fetching ${chartType} chart data for timeframe: ${timeframe}`);
      const response = await dashboardAPI.getChartData(chartType, timeframe);
      console.log(`✅ ${chartType} chart data loaded:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching ${chartType} chart data:`, error);
      
      if (error.response?.status === 400) {
        throw new Error(`Invalid chart type: ${chartType}`);
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view chart data.');
      } else {
        throw new Error(error.response?.data?.message || `Failed to load ${chartType} chart data`);
      }
    }
  },

  /**
   * NPS gauge + trend + promoter/passive/detractor breakdown, from real
   * NetPromoterScore question responses (see DashboardController.GetNpsSummary).
   */
  async getNpsSummary() {
    try {
      const response = await dashboardAPI.getNpsSummary();
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching NPS summary:', error);
      throw new Error(error.response?.data?.message || 'Failed to load NPS summary');
    }
  },

  /**
   * Satisfaction breakdown (Very dissatisfied..Very satisfied), from real
   * RatingScale question responses.
   */
  async getSatisfactionSummary() {
    try {
      const response = await dashboardAPI.getSatisfactionSummary();
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching satisfaction summary:', error);
      throw new Error(error.response?.data?.message || 'Failed to load satisfaction summary');
    }
  },

  /**
   * Sentiment donut (Positive/Neutral/Mixed/Negative), derived from the same
   * RatingScale responses grouped per survey submission.
   */
  async getSentimentSummary() {
    try {
      const response = await dashboardAPI.getSentimentSummary();
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching sentiment summary:', error);
      throw new Error(error.response?.data?.message || 'Failed to load sentiment summary');
    }
  },

  /**
   * Most recent open-text feedback (TextArea/TextField question responses).
   */
  async getFeedback(take = 20) {
    try {
      const response = await dashboardAPI.getFeedback(take);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching feedback:', error);
      throw new Error(error.response?.data?.message || 'Failed to load feedback');
    }
  },

  /**
   * Get employee average report data (similar to backend EmployeeAverageReportsController)
   */
  async getEmployeeAverageReport(surveyId, filters = {}) {
    try {
      console.log(`📊 Fetching employee average report for survey ${surveyId}`, filters);
      
      const requestData = {
        surveyId,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        employeeIds: filters.employeeIds
      };

      const response = await dashboardAPI.generateEmployeeAverageReport(requestData);
      console.log('✅ Employee average report loaded:', response.data);
      
      // Enhance the report data with frontend-specific properties
      return this.enhanceEmployeeAverageReport(response.data);
    } catch (error) {
      console.error('❌ Error fetching employee average report:', error);
      
      if (error.response?.status === 400) {
        throw new Error('Invalid request parameters for employee report');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication required. Please login again.');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to view employee reports.');
      } else if (error.response?.status === 404) {
        throw new Error('Survey not found or access denied.');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load employee average report');
      }
    }
  },

  /**
   * Enhance employee average report with frontend properties
   */
  enhanceEmployeeAverageReport(reportData) {
    if (!reportData || !reportData.categories) {
      return reportData;
    }

    // Add performance indicators and visual properties
    const enhancedCategories = reportData.categories.map(category => ({
      ...category,
      performanceLevel: this.getPerformanceLevel(category.averageScore),
      color: this.getCategoryColor(category.averageScore),
      trend: this.calculateTrend(category.averageScore, 4.0), // Target 4.0
      // Enhanced question data
      questions: category.questions?.map(question => ({
        ...question,
        performanceLevel: this.getPerformanceLevel(question.averageScore),
        color: this.getQuestionColor(question.averageScore),
        trend: this.calculateTrend(question.averageScore, 4.0),
        // Enhanced rating distribution for charts
        ratingDistributionChart: this.formatRatingDistributionForChart(question.ratingDistribution)
      })) || []
    }));

    return {
      ...reportData,
      categories: enhancedCategories,
      overallPerformance: this.getPerformanceLevel(reportData.overallScore),
      // Add summary metrics
      summary: {
        totalQuestions: enhancedCategories.reduce((sum, cat) => sum + (cat.questions?.length || 0), 0),
        averageResponseRate: reportData.responseRate,
        highPerformingCategories: enhancedCategories.filter(cat => cat.averageScore >= 4.0).length,
        needsImprovementCategories: enhancedCategories.filter(cat => cat.averageScore < 3.0).length
      }
    };
  },

  /**
   * Format rating distribution for chart display
   */
  formatRatingDistributionForChart(ratingDistribution) {
    if (!ratingDistribution) return [];
    
    return [
      { rating: 1, percentage: ratingDistribution.rating1, label: '1 Star', color: '#ff4d4f' },
      { rating: 2, percentage: ratingDistribution.rating2, label: '2 Stars', color: '#ff7a45' },
      { rating: 3, percentage: ratingDistribution.rating3, label: '3 Stars', color: '#ffa940' },
      { rating: 4, percentage: ratingDistribution.rating4, label: '4 Stars', color: '#ffec3d' },
      { rating: 5, percentage: ratingDistribution.rating5, label: '5 Stars', color: '#52c41a' }
    ];
  },

  /**
   * Get category color based on average score
   */
  getCategoryColor(score) {
    if (score >= 4.0) return '#10B981'; // Excellent - green
    if (score >= 3.5) return '#3B82F6'; // Good - blue
    if (score >= 3.0) return '#F59E0B'; // Average - yellow
    if (score >= 2.5) return '#EF4444'; // Needs improvement - red
    return '#6B7280'; // Poor - gray
  },

  /**
   * Get question color based on average score
   */
  getQuestionColor(score) {
    if (score >= 4.0) return '#34D399'; // Light green
    if (score >= 3.5) return '#60A5FA'; // Light blue
    if (score >= 3.0) return '#FBBF24'; // Light yellow
    if (score >= 2.5) return '#F87171'; // Light red
    return '#9CA3AF'; // Light gray
  },

  /**
   * Get all chart data for dashboard (bulk operation) - UPDATED with employee report integration
   */
  async getAllChartData(timeframe = '30d', options = {}) {
    try {
      console.log('📊 Fetching all chart data for dashboard...');
      
      const chartTypes = [
        'bullet',
        'dotplot', 
        'radialbar',
        'nightingale',
        'stackedbar',
        'stackedcolumn',
        'plot',
        'categoryoverview',
        'questionoverview'
      ];

      // Fetch all chart data in parallel
      const chartPromises = chartTypes.map(chartType => 
        this.getChartData(chartType, timeframe).catch(error => {
          console.warn(`⚠️ Failed to load ${chartType} chart:`, error.message);
          return { 
            chartType, 
            data: [], 
            categories: [], 
            timeframe,
            error: error.message,
            timestamp: new Date().toISOString()
          };
        })
      );

      // Add employee average report if survey ID is provided
      if (options.surveyId) {
        chartPromises.push(
          this.getEmployeeAverageReport(options.surveyId, options.filters).catch(error => {
            console.warn('⚠️ Failed to load employee average report:', error.message);
            return {
              chartType: 'employeeReport',
              data: [],
              categories: [],
              error: error.message,
              timestamp: new Date().toISOString()
            };
          })
        );
      }

      const chartResults = await Promise.all(chartPromises);
      
      // Transform results into a map for easy access
      const chartDataMap = {};
      chartResults.forEach((result) => {
        const chartType = result.chartType;
        chartDataMap[chartType] = {
          ...result,
          // Add enhanced metadata
          hasData: result.data && result.data.length > 0,
          dataPoints: result.data?.length || 0,
          categoriesCount: result.categories?.length || 0,
          // Employee report specific metadata
          ...(chartType === 'employeeReport' && {
            overallScore: result.overallScore,
            totalResponses: result.totalResponses,
            responseRate: result.responseRate
          })
        };
      });

      console.log('✅ All chart data loaded successfully');
      return chartDataMap;
    } catch (error) {
      console.error('❌ Error fetching all chart data:', error);
      throw new Error('Failed to load dashboard charts: ' + error.message);
    }
  },

  /**
   * Get comprehensive dashboard data (stats + recent surveys + charts + employee reports)
   */
  async getDashboardData(timeframe = '30d', options = {}) {
    try {
      console.log('🚀 Fetching comprehensive dashboard data...');
      
      const [stats, recentSurveys, chartData] = await Promise.all([
        this.getDashboardStats().catch(error => {
          console.warn('⚠️ Failed to load stats, using fallback:', error.message);
          return this.getFallbackStats();
        }),
        this.getRecentSurveys().catch(error => {
          console.warn('⚠️ Failed to load recent surveys, using fallback:', error.message);
          return [];
        }),
        this.getAllChartData(timeframe, options).catch(error => {
          console.warn('⚠️ Failed to load chart data, using fallback:', error.message);
          return this.getFallbackChartData();
        })
      ]);

      const dashboardData = {
        stats,
        recentSurveys,
        chartData,
        timeframe,
        lastUpdated: new Date().toISOString(),
        // Enhanced data quality metrics
        dataQuality: {
          statsLoaded: !!stats,
          surveysLoaded: Array.isArray(recentSurveys),
          chartsLoaded: Object.keys(chartData).length,
          totalDataPoints: Object.values(chartData).reduce((sum, chart) => sum + (chart.data?.length || 0), 0),
          hasEmployeeReport: !!chartData.employeeReport
        }
      };

      console.log('✅ Comprehensive dashboard data loaded');
      return dashboardData;
    } catch (error) {
      console.error('❌ Error fetching comprehensive dashboard data:', error);
      throw new Error('Failed to load dashboard data: ' + error.message);
    }
  },

  /**
   * Get enhanced plot chart data with questions and categories
   */
  async getPlotChartData(timeframe = '30d', options = {}) {
    try {
      console.log('📊 Fetching enhanced plot chart data with questions and categories...');
      
      const { includeQuestions = true, includeCategories = true, maxDataPoints = 25 } = options;
      
      const plotData = await this.getChartData('plot', timeframe);
      
      // Enhance plot data with additional processing
      const enhancedData = this.enhancePlotChartData(plotData, options);
      
      console.log('✅ Enhanced plot chart data loaded:', {
        dataPoints: enhancedData.data?.length || 0,
        categories: enhancedData.categories?.length || 0,
        questions: enhancedData.questions?.length || 0
      });
      
      return enhancedData;
    } catch (error) {
      console.error('❌ Error fetching plot chart data:', error);
      throw new Error('Failed to load plot chart data: ' + error.message);
    }
  },

  /**
   * Enhance plot chart data with additional processing
   */
  enhancePlotChartData(plotData, options = {}) {
    if (!plotData || !plotData.data) {
      return plotData;
    }

    const { maxDataPoints = 25, groupByType = true } = options;
    
    // Separate categories and questions data
    const categoriesData = plotData.data.filter(item => 
      item.label && item.label.startsWith('Category:')
    );
    
    const questionsData = plotData.data.filter(item => 
      item.label && item.label.startsWith('Q:')
    );

    // Sort by value (performance)
    const sortedCategories = [...categoriesData].sort((a, b) => b.value - a.value);
    const sortedQuestions = [...questionsData].sort((a, b) => b.value - a.value);

    // Limit data points if needed
    const limitedCategories = maxDataPoints ? 
      sortedCategories.slice(0, Math.floor(maxDataPoints / 2)) : sortedCategories;
    
    const limitedQuestions = maxDataPoints ? 
      sortedQuestions.slice(0, Math.floor(maxDataPoints / 2)) : sortedQuestions;

    // Enhance data with additional properties
    const enhancedCategories = limitedCategories.map(item => ({
      ...item,
      type: 'category',
      shortLabel: item.label.replace('Category: ', ''),
      performance: this.getPerformanceLevel(item.value),
      trend: this.calculateTrend(item.value, item.target),
      color: this.getCategoryColor(item.value)
    }));

    const enhancedQuestions = limitedQuestions.map(item => ({
      ...item,
      type: 'question',
      shortLabel: item.label.replace('Q: ', ''),
      performance: this.getPerformanceLevel(item.value),
      trend: this.calculateTrend(item.value, item.target),
      color: this.getQuestionColor(item.value),
      // Extract category from question data if available
      category: this.extractCategoryFromQuestion(item.label)
    }));

    // Group data if requested
    let groupedData = [];
    if (groupByType) {
      groupedData = [
        ...enhancedCategories,
        ...enhancedQuestions
      ];
    } else {
      groupedData = [
        ...enhancedCategories,
        ...enhancedQuestions
      ].sort((a, b) => b.value - a.value);
    }

    return {
      ...plotData,
      data: groupedData,
      metadata: {
        categoriesCount: enhancedCategories.length,
        questionsCount: enhancedQuestions.length,
        averageCategoryRating: enhancedCategories.length > 0 ? 
          enhancedCategories.reduce((sum, item) => sum + item.value, 0) / enhancedCategories.length : 0,
        averageQuestionRating: enhancedQuestions.length > 0 ? 
          enhancedQuestions.reduce((sum, item) => sum + item.value, 0) / enhancedQuestions.length : 0,
        highestRatedCategory: enhancedCategories[0],
        lowestRatedCategory: enhancedCategories[enhancedCategories.length - 1],
        highestRatedQuestion: enhancedQuestions[0],
        lowestRatedQuestion: enhancedQuestions[enhancedQuestions.length - 1]
      }
    };
  },

  /**
   * Get performance level based on rating value
   */
  getPerformanceLevel(rating) {
    if (rating >= 4.0) return 'excellent';
    if (rating >= 3.5) return 'good';
    if (rating >= 3.0) return 'average';
    if (rating >= 2.5) return 'needs-improvement';
    return 'poor';
  },

  /**
   * Calculate trend based on current value and target
   */
  calculateTrend(value, target) {
    if (!target) return 'neutral';
    const percentage = (value / target) * 100;
    if (percentage >= 90) return 'exceeding';
    if (percentage >= 80) return 'meeting';
    return 'below';
  },

  /**
   * Extract category from question label
   */
  extractCategoryFromQuestion(questionLabel) {
    const match = questionLabel.match(/\[(.*?)\]/);
    return match ? match[1] : 'General';
  },

  /**
   * Get questions by category for the plot chart
   */
  async getQuestionsByCategory(timeframe = '30d') {
    try {
      const plotData = await this.getPlotChartData(timeframe);
      
      const questionsByCategory = {};
      
      plotData.data.forEach(item => {
        if (item.type === 'question' && item.category) {
          if (!questionsByCategory[item.category]) {
            questionsByCategory[item.category] = [];
          }
          questionsByCategory[item.category].push(item);
        }
      });
      
      // Sort questions within each category by rating
      Object.keys(questionsByCategory).forEach(category => {
        questionsByCategory[category].sort((a, b) => b.value - a.value);
      });
      
      return questionsByCategory;
    } catch (error) {
      console.error('❌ Error getting questions by category:', error);
      return {};
    }
  },

  /**
   * Get employee report summary for quick insights
   */
  async getEmployeeReportSummary(surveyId) {
    try {
      const report = await this.getEmployeeAverageReport(surveyId);
      
      return {
        overallScore: report.overallScore,
        performanceLevel: report.overallPerformance,
        totalResponses: report.totalResponses,
        responseRate: report.responseRate,
        categoriesCount: report.categories.length,
        highPerformingCategories: report.summary.highPerformingCategories,
        needsImprovementCategories: report.summary.needsImprovementCategories,
        topCategory: report.categories[0],
        bottomCategory: report.categories[report.categories.length - 1]
      };
    } catch (error) {
      console.error('❌ Error getting employee report summary:', error);
      throw error;
    }
  },

  /**
   * Compare multiple employee reports
   */
  async compareEmployeeReports(surveyIds, filters = {}) {
    try {
      console.log('📊 Comparing employee reports for surveys:', surveyIds);
      
      const reportPromises = surveyIds.map(surveyId => 
        this.getEmployeeAverageReport(surveyId, filters)
      );
      
      const reports = await Promise.all(reportPromises);
      
      const comparison = {
        surveys: reports.map(report => ({
          surveyId: report.surveyId,
          overallScore: report.overallScore,
          performanceLevel: report.overallPerformance,
          totalResponses: report.totalResponses,
          responseRate: report.responseRate,
          categories: report.categories
        })),
        summary: {
          averageScore: reports.reduce((sum, report) => sum + report.overallScore, 0) / reports.length,
          highestScore: Math.max(...reports.map(report => report.overallScore)),
          lowestScore: Math.min(...reports.map(report => report.overallScore)),
          totalResponses: reports.reduce((sum, report) => sum + report.totalResponses, 0)
        }
      };
      
      return comparison;
    } catch (error) {
      console.error('❌ Error comparing employee reports:', error);
      throw error;
    }
  },

  /**
   * Refresh specific chart data
   */
  async refreshChart(chartType, timeframe = '30d', options = {}) {
    try {
      console.log(`🔄 Refreshing ${chartType} chart data...`);
      
      let chartData;
      if (chartType === 'plot') {
        chartData = await this.getPlotChartData(timeframe, options);
      } else if (chartType === 'employeeReport') {
        chartData = await this.getEmployeeAverageReport(options.surveyId, options.filters);
      } else {
        chartData = await this.getChartData(chartType, timeframe);
      }
      
      console.log(`✅ ${chartType} chart refreshed`);
      return chartData;
    } catch (error) {
      console.error(`❌ Error refreshing ${chartType} chart:`, error);
      throw error;
    }
  },

  /**
   * Get chart data with retry mechanism
   */
  async getChartDataWithRetry(chartType, timeframe = '30d', maxRetries = 3, options = {}) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Attempt ${attempt} to fetch ${chartType} chart data...`);
        
        if (chartType === 'plot') {
          return await this.getPlotChartData(timeframe, options);
        } else if (chartType === 'employeeReport') {
          return await this.getEmployeeAverageReport(options.surveyId, options.filters);
        } else {
          return await this.getChartData(chartType, timeframe);
        }
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
          console.log(`⏳ Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  },

  /**
   * Validate dashboard data completeness
   */
  validateDashboardData(dashboardData) {
    const issues = [];

    if (!dashboardData.stats) {
      issues.push('Missing statistics data');
    }

    if (!dashboardData.recentSurveys) {
      issues.push('Missing recent surveys data');
    }

    if (!dashboardData.chartData) {
      issues.push('Missing chart data');
    } else {
      const expectedCharts = [
        'bullet', 'dotplot', 'radialbar', 'nightingale', 
        'stackedbar', 'stackedcolumn', 'plot'
      ];
      
      const missingCharts = expectedCharts.filter(chart => !dashboardData.chartData[chart]);
      const emptyCharts = expectedCharts.filter(chart => 
        dashboardData.chartData[chart] && 
        (!dashboardData.chartData[chart].data || dashboardData.chartData[chart].data.length === 0)
      );
      
      if (missingCharts.length > 0) {
        issues.push(`Missing charts: ${missingCharts.join(', ')}`);
      }
      
      if (emptyCharts.length > 0) {
        issues.push(`Empty charts: ${emptyCharts.join(', ')}`);
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
      timestamp: dashboardData.lastUpdated,
      score: issues.length === 0 ? 100 : Math.max(0, 100 - (issues.length * 10))
    };
  },

  /**
   * Get dashboard summary for quick overview
   */
  async getDashboardSummary(options = {}) {
    try {
      const dashboardData = await this.getDashboardData('30d', options);
      const validation = this.validateDashboardData(dashboardData);

      // Calculate additional metrics for plot chart
      const plotChart = dashboardData.chartData?.plot;
      const plotMetrics = plotChart ? {
        categoriesWithData: plotChart.data?.filter(item => item.type === 'category').length || 0,
        questionsWithData: plotChart.data?.filter(item => item.type === 'question').length || 0,
        averageCategoryRating: plotChart.metadata?.averageCategoryRating || 0,
        averageQuestionRating: plotChart.metadata?.averageQuestionRating || 0
      } : null;

      // Employee report metrics
      const employeeReport = dashboardData.chartData?.employeeReport;
      const employeeMetrics = employeeReport ? {
        overallScore: employeeReport.overallScore,
        responseRate: employeeReport.responseRate,
        categoriesCount: employeeReport.categories?.length || 0
      } : null;

      const summary = {
        stats: dashboardData.stats,
        recentSurveysCount: dashboardData.recentSurveys?.length || 0,
        chartsLoaded: Object.keys(dashboardData.chartData || {}).length,
        plotChartMetrics: plotMetrics,
        employeeReportMetrics: employeeMetrics,
        validation,
        lastUpdated: dashboardData.lastUpdated,
        dataQuality: dashboardData.dataQuality
      };

      return summary;
    } catch (error) {
      console.error('❌ Error generating dashboard summary:', error);
      throw error;
    }
  },

  /**
   * Export dashboard data for reporting
   */
  async exportDashboardData(format = 'json', options = {}) {
    try {
      console.log(`📤 Exporting dashboard data as ${format}...`);
      
      const dashboardData = await this.getDashboardData('30d', options);
      
      if (format === 'json') {
        // Create downloadable JSON file
        const dataStr = JSON.stringify(dashboardData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dashboard-export-${new Date().getTime()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('✅ Dashboard data exported as JSON');
        return { success: true, format: 'json' };
      } else if (format === 'csv') {
        // Export plot chart data as CSV
        await this.exportPlotChartDataAsCSV(dashboardData.chartData?.plot);
        return { success: true, format: 'csv' };
      } else if (format === 'employee-report') {
        // Export employee report as CSV
        if (options.surveyId) {
          const report = await this.getEmployeeAverageReport(options.surveyId, options.filters);
          await this.exportEmployeeReportAsCSV(report);
        }
        return { success: true, format: 'employee-report' };
      } else {
        throw new Error(`Export format ${format} not supported`);
      }
    } catch (error) {
      console.error('❌ Error exporting dashboard data:', error);
      throw new Error('Failed to export dashboard data: ' + error.message);
    }
  },

  /**
   * Export plot chart data as CSV
   */
  async exportPlotChartDataAsCSV(plotData) {
    if (!plotData || !plotData.data) {
      throw new Error('No plot chart data available for export');
    }

    const headers = ['Type', 'Label', 'Rating', 'Responses', 'Performance', 'Trend', 'Category'];
    const csvRows = [headers.join(',')];

    plotData.data.forEach(item => {
      const row = [
        item.type,
        `"${item.label.replace('"', '""')}"`, // Escape quotes
        item.value,
        item.completed || 0,
        item.performance || '',
        item.trend || '',
        item.category || ''
      ];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plot-chart-data-${new Date().getTime()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Export employee report as CSV
   */
  async exportEmployeeReportAsCSV(employeeReport) {
    if (!employeeReport || !employeeReport.categories) {
      throw new Error('No employee report data available for export');
    }

    const headers = ['Category', 'Average Score', 'Performance', 'Questions Count', 'Trend'];
    const csvRows = [headers.join(',')];

    employeeReport.categories.forEach(category => {
      const row = [
        `"${category.categoryName.replace('"', '""')}"`,
        category.averageScore,
        category.performanceLevel,
        category.questions?.length || 0,
        category.trend
      ];
      csvRows.push(row.join(','));
    });

    // Add summary row
    csvRows.push('');
    csvRows.push('Summary,,,,');
    csvRows.push(`Overall Score,${employeeReport.overallScore},,,`);
    csvRows.push(`Total Responses,${employeeReport.totalResponses},,,`);
    csvRows.push(`Response Rate,${employeeReport.responseRate}%,,,`);

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `employee-report-${new Date().getTime()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Get available timeframes for charts
   */
  getAvailableTimeframes() {
    return [
      { value: '7d', label: 'Last 7 Days' },
      { value: '30d', label: 'Last 30 Days' },
      { value: '90d', label: 'Last 90 Days' },
      { value: '1y', label: 'Last 1 Year' }
    ];
  },

  /**
   * Get chart configuration options - UPDATED with employee report
   */
  getChartConfig(chartType) {
    const defaultConfigs = {
      bullet: {
        height: 300,
        colors: ['#8884d8', '#82ca9d', '#ffc658'],
        showLegend: true
      },
      dotplot: {
        height: 400,
        dotSize: 8,
        colors: ['#0088fe', '#00c49f'],
        showGrid: true
      },
      radialbar: {
        height: 400,
        width: 400,
        colors: ['#8884d8', '#82ca9d', '#ffc658', '#ff8042'],
        innerRadius: 50,
        outerRadius: 150
      },
      nightingale: {
        height: 500,
        width: 500,
        colors: ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe']
      },
      stackedbar: {
        height: 400,
        colors: ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'],
        showLegend: true,
        horizontal: false
      },
      stackedcolumn: {
        height: 400,
        colors: ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe'],
        showLegend: true,
        showValues: true
      },
      plot: {
        height: 500,
        width: 800,
        colors: {
          category: '#8884d8',
          question: '#82ca9d',
          excellent: '#10B981',
          good: '#3B82F6',
          average: '#F59E0B',
          'needs-improvement': '#EF4444',
          poor: '#6B7280'
        },
        showLegend: true,
        showGrid: true,
        pointSize: 6,
        showTrendLines: true,
        categorySettings: {
          pointShape: 'circle',
          pointSize: 8,
          color: '#8884d8'
        },
        questionSettings: {
          pointShape: 'triangle',
          pointSize: 6,
          color: '#82ca9d'
        }
      },
      // NEW: Employee report chart configuration
      employeeReport: {
        height: 600,
        colors: {
          excellent: '#10B981',
          good: '#3B82F6',
          average: '#F59E0B',
          'needs-improvement': '#EF4444',
          poor: '#6B7280'
        },
        showCategoryScores: true,
        showQuestionDetails: false,
        ratingDistribution: {
          colors: ['#ff4d4f', '#ff7a45', '#ffa940', '#ffec3d', '#52c41a']
        }
      }
    };

    return defaultConfigs[chartType] || {};
  },

  /**
   * Format chart data for specific visualization libraries - UPDATED with employee report
   */
  formatChartDataForLibrary(chartData, library = 'recharts') {
    if (!chartData || !chartData.data) {
      return { data: [], categories: [] };
    }

    switch (library) {
      case 'recharts':
        return this.formatForRecharts(chartData);
      case 'chartjs':
        return this.formatForChartJS(chartData);
      case 'apexcharts':
        return this.formatForApexCharts(chartData);
      default:
        return chartData;
    }
  },

  /**
   * Format data for Recharts library - UPDATED with employee report
   */
  formatForRecharts(chartData) {
    const { data, categories, chartType } = chartData;
    
    switch (chartType) {
      case 'bullet':
        return {
          data: data.map(item => ({
            name: item.label,
            value: item.value,
            target: item.target
          })),
          config: {
            dataKey: 'value',
            targetKey: 'target'
          }
        };
        
      case 'dotplot':
        return {
          data: data.map(item => ({
            name: item.label,
            value: item.value
          })),
          config: {
            dataKey: 'value',
            nameKey: 'name'
          }
        };
        
      case 'radialbar':
        return {
          data: data.map(item => ({
            name: item.label,
            value: item.value
          })),
          config: {
            dataKey: 'value',
            nameKey: 'name'
          }
        };

      case 'plot':
        return {
          data: data.map(item => ({
            ...item,
            name: item.shortLabel || item.label,
            fill: this.getColorForPerformance(item.performance),
            stroke: this.getColorForPerformance(item.performance),
            symbol: item.type === 'category' ? 'circle' : 'triangle'
          })),
          config: {
            dataKey: 'value',
            nameKey: 'name',
            categoryKey: 'type',
            performanceKey: 'performance'
          }
        };

      case 'employeeReport':
        return {
          data: data.map(item => ({
            ...item,
            name: item.categoryName,
            score: item.averageScore,
            fill: this.getColorForPerformance(item.performanceLevel),
            questions: item.questions?.length || 0
          })),
          config: {
            dataKey: 'score',
            nameKey: 'name',
            questionsKey: 'questions'
          }
        };
        
      default:
        return { data, categories };
    }
  },

  /**
   * Get color based on performance level
   */
  getColorForPerformance(performance) {
    const colors = {
      excellent: '#10B981',
      good: '#3B82F6',
      average: '#F59E0B',
      'needs-improvement': '#EF4444',
      poor: '#6B7280'
    };
    return colors[performance] || '#6B7280';
  },

  /**
   * Format data for Chart.js library
   */
  formatForChartJS(chartData) {
    // Implementation for Chart.js format
    return chartData;
  },

  /**
   * Format data for ApexCharts library
   */
  formatForApexCharts(chartData) {
    // Implementation for ApexCharts format
    return chartData;
  },

  /**
   * Get performance metrics for monitoring
   */
  async getPerformanceMetrics() {
    try {
      const startTime = performance.now();
      const dashboardData = await this.getDashboardData();
      const endTime = performance.now();
      
      const loadTime = endTime - startTime;
      const dataSize = JSON.stringify(dashboardData).length;
      
      // Calculate plot chart specific metrics
      const plotChart = dashboardData.chartData?.plot;
      const plotMetrics = plotChart ? {
        categoriesCount: plotChart.metadata?.categoriesCount || 0,
        questionsCount: plotChart.metadata?.questionsCount || 0,
        avgCategoryRating: plotChart.metadata?.averageCategoryRating || 0,
        avgQuestionRating: plotChart.metadata?.averageQuestionRating || 0
      } : null;

      // Employee report metrics
      const employeeReport = dashboardData.chartData?.employeeReport;
      const employeeMetrics = employeeReport ? {
        overallScore: employeeReport.overallScore,
        categoriesCount: employeeReport.categories?.length || 0
      } : null;

      return {
        loadTime: Math.round(loadTime),
        dataSize,
        dataSizeKB: Math.round(dataSize / 1024),
        chartsCount: Object.keys(dashboardData.chartData || {}).length,
        surveysCount: dashboardData.recentSurveys?.length || 0,
        plotChartMetrics: plotMetrics,
        employeeReportMetrics: employeeMetrics,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error getting performance metrics:', error);
      throw error;
    }
  },

  /**
   * Fallback data for when API calls fail
   */
  getFallbackStats() {
    return {
      totalEmployees: 0,
      totalCompanies: 0,
      activeSurveys: 0,
      totalCategories: 0,
      totalQuestions: 0,
      totalResponses: 0,
      pendingInvitations: 0,
      averageResponseRate: 0,
      completionRate: 0,
      isFallback: true
    };
  },

  /**
   * Fallback chart data for when API calls fail
   */
  getFallbackChartData() {
    const chartTypes = [
      'bullet', 'dotplot', 'radialbar', 'nightingale', 
      'stackedbar', 'stackedcolumn', 'plot', 'categoryoverview', 'questionoverview', 'employeeReport'
    ];
    
    const fallbackData = {};
    chartTypes.forEach(chartType => {
      fallbackData[chartType] = {
        chartType,
        data: [],
        categories: [],
        timeframe: '30d',
        isFallback: true,
        error: 'Failed to load data from server'
      };
    });
    
    return fallbackData;
  }
};

// Export as default for backward compatibility
export default dashboardService;